import { styled, Typography } from "@mui/material";
import React from "react";
import { useTheme } from "../../../ThemeContext/ThemeContext";

// Helper function to create themed typography wrapper components
const createThemedTypography = (
  baseStyles: any, 
  colorType: 'primary' | 'secondary' | 'label' | 'data' = 'primary'
) => {
  const StyledComponent = styled(Typography)<{ color?: string }>(({ color }) => ({
    ...baseStyles,
    color: color || '#000', // fallback color
  }));

  // Wrapper component that uses theme
  const ThemedComponent = React.forwardRef<any, any>((props, ref) => {
    const { colors } = useTheme();
    
    let themeColor;
    switch (colorType) {
      case 'secondary':
        themeColor = colors.text.secondary;
        break;
      case 'label':
        themeColor = colors.text.label;
        break;
      case 'data':
        themeColor = colors.text.data;
        break;
      default:
        themeColor = colors.text.primary;
    }
    
    return <StyledComponent ref={ref} color={props.color || themeColor} {...props} />;
  });

  return ThemedComponent;
};

const H1DisplayLarge = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "3.5625rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "4rem",
  letterSpacing: "-0.01563rem",
});

const H2DisplayMedium = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "2.8125rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "3.25rem",
});

const H3DisplaySmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "2.25rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "2.75rem",
});

const H4HeadlineLarge = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "2rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "2.5rem",
});

const H5HeadlineMedium = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "1.75rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "2.25rem",
});

const H6HeadlineSmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "1.5rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "2rem",
});

const H7TitleLarge = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "1.375rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "1.75rem",
});

const H8TitleMedium = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "1rem",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "1.5rem",
  letterSpacing: "0.00938rem",
});

const H9TitleSmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "1.25rem",
  letterSpacing: "0.00625rem",
});

const LabelLarge = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "1.25rem",
  letterSpacing: "0.00625rem",
}, 'label');

const LabelMedium = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "1rem",
  letterSpacing: "0.03125rem",
}, 'label');

const LabelSmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.6875rem",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "1rem",
  letterSpacing: "0.03125rem",
}, 'label');

const BodyLarge = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "1rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "1.5rem",
  letterSpacing: "0.03125rem",
});

const BodyMedium = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "1.25rem",
  letterSpacing: "0.01563rem",
});

const BodySmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "1rem",
  letterSpacing: "0.025rem",
});

const H10TitleXSmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.5rem",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "0.75rem",
  letterSpacing: "0.00469rem",
});

const BodyXSmall = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.4rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "0.5rem",
  letterSpacing: "0.013rem",
});

const DataLegend = createThemedTypography({
  fontFamily: "Roboto",
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "0.875rem",
}, 'data');

export {
  H1DisplayLarge,
  H2DisplayMedium,
  H3DisplaySmall,
  H4HeadlineLarge,
  H5HeadlineMedium,
  H6HeadlineSmall,
  H7TitleLarge,
  H8TitleMedium,
  H9TitleSmall,
  H10TitleXSmall,
  LabelLarge,
  LabelMedium,
  LabelSmall,
  BodyLarge,
  BodyMedium,
  BodySmall,
  BodyXSmall,
  DataLegend,
};
